import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Globe, Send, Loader2, Calculator, ArrowUpRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", vehicle: "", service: "Stage 1", message: "" };

const SERVICE_OPTIONS = [
  "Stage 1",
  "Stage 1+",
  "Stage 2",
  "Stage 3",
  "Optimisation boîte TCU",
  "Stage 1 FlexFuel E85",
  "FlexFuel E85 (cartographie seule)",
  "Diagnostic complet",
  "Effacement des défauts",
  "Régénération FAP",
  "Remise à zéro",
  "Codage injecteur / clé",
  "Forfait déplacement",
  "Options (EGR, FAP, Pop&Bang…)",
  "Autre demande",
];

const inputCls =
  "w-full bg-transparent border border-[#454C50] focus:border-[#6EB000] outline-none px-4 py-3 text-sm text-white placeholder:text-white/30 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const s = searchParams.get("service");
    if (s && SERVICE_OPTIONS.includes(s)) {
      setForm((f) => ({ ...f, service: s }));
    }
  }, [searchParams]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! Nicolas vous recontacte rapidement.");
      setForm(initial);
    } catch (err) {
      toast.error("Erreur d'envoi. Appelez le 07 81 90 12 48.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <p className="font-script text-2xl text-[#6EB000] mb-2">Parlons de votre véhicule</p>
          <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-white">
            Demandez votre <span className="text-[#6EB000]">étude</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input required placeholder="Votre nom *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
              <input required placeholder="Téléphone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
              <input placeholder="Véhicule (ex : Golf 7 1.4 TSI)" value={form.vehicle} onChange={set("vehicle")} className={inputCls} data-testid="contact-vehicle-input" />
            </div>
            <div data-testid="contact-service-select">
              <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Prestation souhaitée *</p>
              <div className="flex flex-wrap gap-2">
                {SERVICE_OPTIONS.map((o, i) => {
                  const active = form.service === o;
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() => setForm({ ...form, service: o })}
                      className={`px-4 py-2 text-xs sm:text-sm uppercase tracking-wider transition-all duration-200 ${
                        active
                          ? "border-2 border-[#6EB000] bg-[#6EB000] text-black font-bold"
                          : "border border-[#454C50] text-white/70 hover:border-[#6EB000]/60 hover:text-white"
                      }`}
                      data-testid={`contact-service-chip-${i}`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>

            <a
              href="/configurateur"
              className="flex items-center justify-between gap-4 border border-[#6EB000]/40 bg-[#6EB000]/5 hover:bg-[#6EB000]/10 p-5 transition-colors duration-300"
              data-testid="contact-configurator-cta"
            >
              <div className="flex items-center gap-3">
                <Calculator className="w-5 h-5 text-[#6EB000] shrink-0" />
                <div>
                  <p className="text-sm font-bold text-white">Configurateur en ligne</p>
                  <p className="text-xs text-white/50">Composez votre prestation au tarif officiel, puis payez par carte bancaire</p>
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-[#6EB000] shrink-0" />
            </a>

            <textarea
              required
              rows={5}
              placeholder="Votre message *"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
              data-testid="contact-message-input"
            />
            <button
              type="submit"
              disabled={sending}
              className="clip-angle inline-flex items-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200 disabled:opacity-50"
              data-testid="contact-submit-button"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Envoi…" : "Envoyer ma demande"}
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-2 space-y-4"
            data-testid="contact-info-panel"
          >
            <p className="font-script text-2xl text-[#F5A623]">Ou contactez-moi directement</p>
            <a
              href="tel:+33781901248"
              className="flex items-center gap-4 border border-[#454C50] hover:border-[#6EB000] p-5 transition-colors duration-300 group"
              data-testid="contact-tel-link"
            >
              <Phone className="w-6 h-6 text-[#6EB000]" strokeWidth={1.6} />
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/40">Téléphone</p>
                <p className="font-display text-xl text-white group-hover:text-[#6EB000] transition-colors">07 81 90 12 48</p>
              </div>
            </a>
            <a
              href="mailto:nbperformance@outlook.fr"
              className="flex items-center gap-4 border border-[#454C50] hover:border-[#6EB000] p-5 transition-colors duration-300 group"
              data-testid="contact-email-link"
            >
              <Mail className="w-6 h-6 text-[#6EB000]" strokeWidth={1.6} />
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/40">Email</p>
                <p className="text-sm text-white group-hover:text-[#6EB000] transition-colors break-all">nbperformance@outlook.fr</p>
              </div>
            </a>
            <a
              href="https://www.nbperformance.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 border border-[#454C50] hover:border-[#6EB000] p-5 transition-colors duration-300 group"
              data-testid="contact-website-link"
            >
              <Globe className="w-6 h-6 text-[#6EB000]" strokeWidth={1.6} />
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/40">Site web</p>
                <p className="text-sm text-white group-hover:text-[#6EB000] transition-colors">www.nbperformance.fr</p>
              </div>
            </a>
            <div className="border border-[#6EB000]/30 bg-[#6EB000]/5 p-5" data-testid="contact-payment-note">
              <p className="text-[11px] uppercase tracking-widest text-white/50 mb-3">Moyens de paiement acceptés</p>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#6EB000] shrink-0" /> Carte bancaire</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#6EB000] shrink-0" /> PayPal</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#6EB000] shrink-0" /> Paiement en plusieurs fois disponible via PayPal, sous réserve d'éligibilité</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#6EB000] shrink-0" /> Espèces sur place</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
